/**
 * A page's written reading, as data.
 *
 * The rules that decide what to say live in pure functions returning these
 * items — translation keys and their numbers, not sentences — so they can be
 * tested without rendering, and the page turns them into InsightList rows
 * with `t()` in one place.
 */
import type { Insight, InsightTone } from '../../../../components/design-system/analytics';

export interface Phrase {
  key: string;
  params?: Record<string, string | number>;
}

export interface ReadingItem {
  id: string;
  tone: InsightTone;
  title: Phrase;
  detail?: Phrase;
  action?: { key: string; href: string };
}

/** Rounded percent change, or null when there is nothing to compare with. */
export function changePercent(current: number | null | undefined, previous: number | null | undefined): number | null {
  if (current == null || previous == null || previous <= 0) return null;
  return Math.round(((current - previous) / previous) * 100);
}

/** A share as a whole percent, or null when the base is too small to mean anything. */
export function sharePercent(part: number | null | undefined, whole: number | null | undefined, minWhole = 1): number | null {
  if (part == null || whole == null || whole < minWhole || whole <= 0) return null;
  return Math.round((part / whole) * 100);
}

type Translate = (key: string, params?: Record<string, unknown>) => string;

/** The reading's items as InsightList rows. Keys are relative to `VIZ_ANALYTICS.READING`. */
export function toInsights(items: ReadingItem[], t: Translate): Insight[] {
  const say = (p: Phrase) => t(`VIZ_ANALYTICS.READING.${p.key}`, p.params);
  return items.map((item) => ({
    id: item.id,
    tone: item.tone,
    title: say(item.title),
    detail: item.detail ? say(item.detail) : undefined,
    action: item.action ? { label: t(`VIZ_ANALYTICS.READING.ACTION_${item.action.key}`), href: item.action.href } : undefined,
  }));
}
